import { useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { isAdminDomain, redirectToDashboard, redirectToAdmin } from '../../lib/domain';

const STAFF_SESSION_KEY = 'looma-staff-session';

function getStaffRedirect(): string | null {
  try {
    const raw = localStorage.getItem(STAFF_SESSION_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw);
    const validatedAt = s.validatedAt ? new Date(s.validatedAt).getTime() : 0;
    if (s.organizationSlug && Date.now() - validatedAt < 24 * 60 * 60 * 1000) {
      return `/${s.organizationSlug}/pos-staff`;
    }
  } catch { /* ignore */ }
  return null;
}

export function RootRedirect() {
  const { user, loading, isSuperAdmin, userRoles, signOut } = useAuth();
  const adminDomain = isAdminDomain();
  // Skip staff session on admin domain (irrelevant)
  const staffRedirect = useMemo(() => (adminDomain ? null : getStaffRedirect()), [adminDomain]);

  // Show loading while checking auth state
  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  // Not logged in — check for staff session before sending to /login
  if (!user) {
    if (staffRedirect) {
      return <Navigate to={staffRedirect} replace />;
    }
    return <Navigate to="/login" replace />;
  }

  // Super admins must use admin.getlooma.app — never dashboard.getlooma.app,
  // even with an already-active session (e.g. a bookmark or a stale tab).
  if (!adminDomain && isSuperAdmin) {
    signOut().then(() => redirectToAdmin());
    return null;
  }

  // Super admin on the admin domain -> go to admin dashboard
  if (isSuperAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  // On admin domain, non-admin users should go to dashboard domain
  if (adminDomain) {
    const slug = userRoles[0]?.organizationSlug;
    redirectToDashboard(slug ? `/${slug}/pos` : '/login');
    return null;
  }

  // Regular user with org access -> go to their org dashboard
  if (userRoles.length > 0 && userRoles[0].organizationSlug) {
    return <Navigate to={`/${userRoles[0].organizationSlug}/pos`} replace />;
  }

  // User with no valid roles -> send to login for org setup (Google OAuth new users)
  return <Navigate to="/login" replace />;
}
